import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { Search, MapPin, Briefcase, ArrowRight, X, SlidersHorizontal, ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";
import useDocumentTitle from "../hooks/useDocumentTitle";

const PAGE_SIZE = 20;
const EXP_MAX = 30; // years cap on the experience slider

const POPULAR_SKILLS = [
  "Selenium", "Playwright", "Cypress", "API Testing", "Postman", "REST Assured",
  "JMeter", "TestNG", "JUnit", "Pytest", "Appium", "Java", "Python",
  "CI/CD", "Jenkins", "Cucumber", "Robot Framework", "Manual Testing",
];
const JOB_TYPES = ["Full-time", "Contract", "Part-time", "Freelance"];

// Preset salary ranges per currency (values are annualised min-max in the currency's base unit).
const SALARY_PRESETS = {
  INR: [
    { key: "any", label: "Any salary", min: null, max: null },
    { key: "0-500000", label: "Under ₹5L", min: 0, max: 500000 },
    { key: "500000-1000000", label: "₹5L – ₹10L", min: 500000, max: 1000000 },
    { key: "1000000-2500000", label: "₹10L – ₹25L", min: 1000000, max: 2500000 },
    { key: "2500000-5000000", label: "₹25L – ₹50L", min: 2500000, max: 5000000 },
    { key: "5000000-", label: "₹50L+", min: 5000000, max: null },
  ],
  USD: [
    { key: "any", label: "Any salary", min: null, max: null },
    { key: "0-50000", label: "Under $50k", min: 0, max: 50000 },
    { key: "50000-100000", label: "$50k – $100k", min: 50000, max: 100000 },
    { key: "100000-150000", label: "$100k – $150k", min: 100000, max: 150000 },
    { key: "150000-250000", label: "$150k – $250k", min: 150000, max: 250000 },
    { key: "250000-", label: "$250k+", min: 250000, max: null },
  ],
};

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");                     // the text typed in the box
  const [appliedQ, setAppliedQ] = useState("");       // the value actually sent to the backend
  const [loading, setLoading] = useState(true);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [location, setLocation] = useState("");
  const [expRange, setExpRange] = useState([0, EXP_MAX]);   // years experience filter [min, max]
  const [jobType, setJobType] = useState("");
  const [salaryCurrency, setSalaryCurrency] = useState("INR");
  const [salaryPreset, setSalaryPreset] = useState("any");
  const [page, setPage] = useState(0);                // 0-indexed page number
  useDocumentTitle("Jobs");

  // Fetch runs ONLY when the user submits a search (button/Enter) or on first mount.
  // No more per-keystroke debounced refetch → no flicker.
  useEffect(() => {
    setLoading(true);
    api.get("/jobs", { params: appliedQ ? { q: appliedQ } : {} })
      .then(({ data }) => setJobs(data || []))
      .finally(() => setLoading(false));
  }, [appliedQ]);

  const runSearch = () => {
    const trimmed = q.trim();
    setAppliedQ(trimmed);
    setPage(0);
  };

  const clearSearch = () => {
    setQ("");
    setAppliedQ("");
    setPage(0);
  };

  const toggleSkill = (s) => {
    setSelectedSkills((prev) => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  const clearAll = () => {
    setSelectedSkills([]);
    setLocation("");
    setExpRange([0, EXP_MAX]);
    setJobType("");
    setSalaryPreset("any");
    setQ("");
    setAppliedQ("");
    setPage(0);
  };

  const filteredJobs = useMemo(() => {
    const [minWant, maxWant] = expRange;
    const expFilterActive = minWant > 0 || maxWant < EXP_MAX;
    const loc = location.trim().toLowerCase();
    const presets = SALARY_PRESETS[salaryCurrency] || SALARY_PRESETS.INR;
    const preset = presets.find(p => p.key === salaryPreset) || presets[0];
    const salaryFilterActive = preset && (preset.min !== null || preset.max !== null);
    return jobs.filter((j) => {
      if (selectedSkills.length > 0) {
        const jobSkills = [...(j.required_skills || []), ...(j.nice_to_have || [])].map(s => s.toLowerCase());
        const hasAll = selectedSkills.every(s => jobSkills.includes(s.toLowerCase()));
        if (!hasAll) return false;
      }
      if (loc && !(j.location || "").toLowerCase().includes(loc)) return false;
      if (expFilterActive) {
        // Overlap test between candidate's desired range [minWant, maxWant]
        // and the job's expected range [experience_min, experience_max].
        const jLo = j.experience_min ?? 0;
        const jHi = j.experience_max ?? 99;
        if (!(jLo <= maxWant && jHi >= minWant)) return false;
      }
      if (jobType && j.job_type !== jobType) return false;
      if (salaryFilterActive) {
        // Only match jobs that have visible salary in the same currency and overlap the preset range
        const jc = (j.salary_currency || "INR").toUpperCase();
        if (jc !== salaryCurrency) return false;
        const jMin = typeof j.salary_min === "number" ? j.salary_min : null;
        const jMax = typeof j.salary_max === "number" ? j.salary_max : null;
        if (jMin === null && jMax === null) return false;
        const jLo = jMin ?? jMax;
        const jHi = jMax ?? jMin;
        const pLo = preset.min ?? 0;
        const pHi = preset.max ?? Number.POSITIVE_INFINITY;
        // ranges overlap if jLo <= pHi AND jHi >= pLo
        if (!(jLo <= pHi && jHi >= pLo)) return false;
      }
      return true;
    });
  }, [jobs, selectedSkills, location, expRange, jobType, salaryCurrency, salaryPreset]);

  const activeFilters =
    (selectedSkills.length > 0 ? 1 : 0) +
    (location ? 1 : 0) +
    ((expRange[0] > 0 || expRange[1] < EXP_MAX) ? 1 : 0) +
    (jobType ? 1 : 0) +
    (salaryPreset !== "any" ? 1 : 0);

  // Reset to page 0 whenever the effective result set changes.
  useEffect(() => {
    setPage(0);
  }, [selectedSkills, location, expRange, jobType, salaryCurrency, salaryPreset]);

  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pageStart = currentPage * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;
  const pageJobs = filteredJobs.slice(pageStart, pageEnd);
  const showingFrom = filteredJobs.length === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(pageEnd, filteredJobs.length);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-1">browse jobs</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-zinc-950">QA & Automation jobs</h1>
          <p className="text-zinc-600 mt-1">Roles curated for software testing professionals.</p>
        </div>

        <form
          className="flex items-stretch gap-2 mb-4"
          onSubmit={(e) => { e.preventDefault(); runSearch(); }}
          role="search"
          data-testid="jobs-search-form"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, skill, or company…"
              className="pl-9 pr-9 h-11"
              data-testid="jobs-search-input"
              autoComplete="off"
            />
            {q && (
              <button
                type="button"
                onClick={clearSearch}
                aria-label="Clear search"
                data-testid="jobs-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Button
            type="submit"
            data-testid="jobs-search-btn"
            className="h-11 px-5 bg-[#002FA7] hover:bg-[#00227A] text-white"
          >
            <Search className="h-4 w-4 mr-2" />
            Search
          </Button>
        </form>

        {appliedQ && (
          <div className="mb-3 text-xs text-zinc-500 tag-mono flex items-center gap-2" data-testid="jobs-applied-query">
            searching for: <span className="text-zinc-950 font-semibold">&ldquo;{appliedQ}&rdquo;</span>
            <button
              type="button"
              onClick={clearSearch}
              className="underline hover:text-[#002FA7]"
              data-testid="jobs-clear-applied"
            >
              clear
            </button>
          </div>
        )}

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-[#002FA7]" />
              <span className="font-display text-sm font-bold tracking-tight text-zinc-950">Filters</span>
              {activeFilters > 0 && <Badge className="tag-mono bg-blue-50 text-blue-700 border border-blue-200">{activeFilters} active</Badge>}
            </div>
            {activeFilters > 0 && (
              <Button size="sm" variant="ghost" onClick={clearAll} data-testid="clear-filters-btn" className="text-zinc-500 hover:text-zinc-900">
                <X className="h-3.5 w-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>

          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            <Input
              value={location}
              onChange={(e)=>setLocation(e.target.value)}
              placeholder="Location (e.g. Bengaluru, Remote)"
              data-testid="filter-location"
              className="h-10"
            />
            <div className="sm:col-span-1 px-1 py-1.5 flex flex-col justify-center">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs uppercase tracking-widest text-zinc-500 tag-mono">experience</span>
                <span className="text-xs text-zinc-900 tag-mono font-semibold" data-testid="filter-exp-range-label">
                  {expRange[0]}
                  {expRange[1] >= EXP_MAX ? "+" : `–${expRange[1]}`} yrs
                </span>
              </div>
              <Slider
                value={expRange}
                min={0}
                max={EXP_MAX}
                step={1}
                minStepsBetweenThumbs={0}
                onValueChange={(v) => setExpRange(v)}
                data-testid="filter-exp-slider"
                aria-label="Years of experience range"
                className="mt-1"
              />
            </div>
            <select
              value={jobType}
              onChange={(e)=>setJobType(e.target.value)}
              data-testid="filter-job-type"
              className="h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20"
            >
              <option value="">Any job type</option>
              {JOB_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1.5">salary currency</div>
              <select
                value={salaryCurrency}
                onChange={(e)=>{ setSalaryCurrency(e.target.value); setSalaryPreset("any"); }}
                data-testid="filter-salary-currency"
                className="h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20"
              >
                <option value="INR">₹ INR (India)</option>
                <option value="USD">$ USD (Global)</option>
              </select>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1.5">salary range</div>
              <select
                value={salaryPreset}
                onChange={(e)=>setSalaryPreset(e.target.value)}
                data-testid="filter-salary-preset"
                className="h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20"
              >
                {(SALARY_PRESETS[salaryCurrency] || SALARY_PRESETS.INR).map(p => (
                  <option key={p.key} value={p.key}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">filter by skill</div>
            <div className="flex flex-wrap gap-1.5">
              {POPULAR_SKILLS.map(s => {
                const active = selectedSkills.includes(s);
                return (
                  <button
                    key={s}
                    onClick={()=>toggleSkill(s)}
                    data-testid={`filter-skill-${s}`}
                    className={`tag-mono px-2 py-1 rounded border transition-colors ${
                      active
                        ? "bg-[#002FA7] text-white border-[#002FA7]"
                        : "bg-white border-zinc-200 text-zinc-700 hover:border-[#002FA7] hover:text-[#002FA7]"
                    }`}
                  >
                    {active && "✓ "}{s}
                  </button>
                );
              })}
            </div>
          </div>
        </Card>

        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-zinc-500 tag-mono" data-testid="jobs-result-count">
            {loading
              ? "Loading…"
              : filteredJobs.length === 0
              ? "0 jobs found"
              : `showing ${showingFrom}–${showingTo} of ${filteredJobs.length} ${filteredJobs.length === 1 ? "job" : "jobs"}`}
          </span>
          {!loading && totalPages > 1 && (
            <span className="text-xs text-zinc-500 tag-mono" data-testid="jobs-page-indicator-top">
              page {currentPage + 1} of {totalPages}
            </span>
          )}
        </div>

        {loading ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : filteredJobs.length === 0 ? (
          <Card className="p-10 text-center">
            <Briefcase className="h-8 w-8 mx-auto text-zinc-300 mb-3" />
            <div className="font-display text-lg font-bold text-zinc-950">No jobs match your filters</div>
            <div className="text-sm text-zinc-500 mt-1">Try clearing some filters or a different search.</div>
          </Card>
        ) : (
          <>
            <div className="space-y-3" data-testid="jobs-list">
              {pageJobs.map((j) => (
                <Link
                  key={j.job_id}
                  to={`/jobs/${j.job_id}`}
                  data-testid={`job-card-${j.job_id}`}
                  className="block group"
                >
                  <Card className="p-5 hover:border-[#002FA7] transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-display text-lg font-bold text-zinc-950 group-hover:text-[#002FA7] transition-colors">
                            {j.title}
                          </div>
                          {j.applied && (
                            <span
                              className="tag-mono text-[10px] font-bold inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex-shrink-0"
                              data-testid={`job-applied-badge-${j.job_id}`}
                            >
                              <CheckCircle2 className="h-3 w-3" /> APPLIED
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-zinc-600 mt-0.5">{j.company_name}</div>
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-zinc-500 tag-mono">
                          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {j.location}</span>
                          <span>·</span>
                          <span>{j.job_type}</span>
                          <span>·</span>
                          <span>{j.experience_min}–{j.experience_max} yrs</span>
                          {j.salary_range && <><span>·</span><span>{j.salary_range}</span></>}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {(j.required_skills || []).slice(0, 6).map(s => (
                            <span key={s} className="tag-mono px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded">{s}</span>
                          ))}
                          {(j.required_skills || []).length > 6 && (
                            <Badge variant="outline" className="text-xs">+{j.required_skills.length - 6}</Badge>
                          )}
                        </div>
                      </div>
                      <ArrowRight className="h-5 w-5 text-zinc-300 group-hover:text-[#002FA7] mt-1 transition-colors" />
                    </div>
                  </Card>
                </Link>
              ))}
            </div>

            {totalPages > 1 && (
              <nav
                className="mt-6 flex items-center justify-between flex-wrap gap-3"
                aria-label="Jobs pagination"
                data-testid="jobs-pagination"
              >
                <span className="text-xs text-zinc-500 tag-mono">
                  page {currentPage + 1} of {totalPages}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                    data-testid="jobs-page-prev"
                    aria-label="Previous page"
                    className="h-9"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                  </Button>
                  <div className="flex items-center gap-1" data-testid="jobs-page-numbers">
                    {getPageWindow(currentPage, totalPages).map((p, i) =>
                      p === "…" ? (
                        <span key={`gap-${i}`} className="px-2 text-xs text-zinc-400">…</span>
                      ) : (
                        <button
                          key={`p-${p}`}
                          type="button"
                          onClick={() => setPage(p)}
                          data-testid={`jobs-page-${p + 1}`}
                          aria-current={currentPage === p ? "page" : undefined}
                          className={`h-9 min-w-[36px] px-2 rounded border tag-mono text-sm transition-colors ${
                            currentPage === p
                              ? "bg-[#002FA7] text-white border-[#002FA7]"
                              : "bg-white border-zinc-200 text-zinc-700 hover:border-[#002FA7] hover:text-[#002FA7]"
                          }`}
                        >
                          {p + 1}
                        </button>
                      )
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1}
                    data-testid="jobs-page-next"
                    aria-label="Next page"
                    className="h-9"
                  >
                    Next <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </nav>
            )}
          </>
        )}
      </main>
    </div>
  );
}

// Returns a compact page-number window like [0, 1, 2, "…", 9] for a11y-friendly pagination.
function getPageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const window = new Set([0, total - 1, current, current - 1, current + 1]);
  const sorted = [...window].filter((n) => n >= 0 && n < total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    out.push(sorted[i]);
    if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) out.push("…");
  }
  return out;
}
